import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";
import {
  TApplicationField,
  TApplicationFieldOption,
} from "./application-fields.hooks";
import { applicationsKeyStore, TPopulatedForm } from "./applications.hooks";
import { TDocumentConfig } from "./forms.hooks";
import { TReviewWithReviewer } from "./level-2-application-approval.hooks";

export const resubmissionKeyStore = {
  resubmissionReview: (id: number | string) => ["resubmission-review", id],
  resubmissionData: (id: number | string) => ["resubmission-data", id],
  nocForm: ["noc-form"],
  allReviews: (...id: (number | string)[]) => ["user-all-reviews", ...id],
} as const;

export function useResubmissionData(applicationId: string | number) {
  return useQuery({
    queryKey: resubmissionKeyStore.resubmissionData(applicationId),
    enabled: !!applicationId,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: {
          nocs?: Record<string, unknown>[];
          review: TReviewWithReviewer;
          docConfigs?: TDocumentConfig[];
          docs?: { document: string; document_config_id: number }[];
          fields: (TApplicationField & {
            options?: TApplicationFieldOption[];
          })[];
        };
      }>("/applications/resubmission-info", {
        params: { applicationId },
      }),
    select: (response) => response?.data?.data,
  });
}

export function useResubmissionReview(applicationId: string | number) {
  return useQuery({
    queryKey: resubmissionKeyStore.resubmissionReview(applicationId),
    enabled: !!applicationId,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TReviewWithReviewer;
      }>("/applications/last-review", {
        params: { applicationId },
      }),
    select: (response) => response?.data?.data,
  });
}

export function useNOCForm(enabled = true) {
  return useQuery({
    enabled,
    queryKey: resubmissionKeyStore.nocForm,
    queryFn: () =>
      _axios.get<{
        success: true;
        hasMore?: boolean;
        data: TPopulatedForm;
      }>("/applications/noc-form"),
    select: (d) => d.data,
  });
}

export function useAllApplicationReviews({ id }: { id: string | number }) {
  return useQuery({
    queryKey: resubmissionKeyStore.allReviews(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TReviewWithReviewer[];
      }>("/applications/all-reviews", { params: { applicationId: id } }),
    select: (result) => result.data?.data,
  });
}

export function useAllApplicationReviewsAdmin({ id }: { id: string | number }) {
  return useQuery({
    queryKey: resubmissionKeyStore.allReviews(id, "admin"),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TReviewWithReviewer[];
      }>("/applications/all-reviews-admin", { params: { applicationId: id } }),
    select: (result) => result.data?.data,
  });
}

export function useSaveResubmissionData() {
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      resubmissionData: {
        fields: Record<string, unknown>;
        nocs: Record<string, unknown>[];
        docs: Record<string, unknown>;
      };
    }) =>
      _axios.post<{
        success: true;
      }>("/applications/save-resubmission-data", data),
    onSuccess: (result) => {
      if (result.data?.success) SuccessToast("Saved!");
      else ErrorToast("Something went wrong while saving data!");
    },
    onError: () => {
      ErrorToast("Something went wrong while saving data!");
    },
  });
}

export function useResubmitApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { applicationId: number }) =>
      _axios.post<{ success: true }>(
        "/applications/resubmit-application",
        data
      ),
    onSuccess: (res) => {
      if (res.data?.success) SuccessToast("Application resubmitted!");
    },
    onError: () => {
      ErrorToast("Something went wrong while resubmitting application!");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: applicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: applicationsKeyStore.counts() });
      qc.invalidateQueries({ queryKey: applicationsKeyStore.stats() });
    },
  });
}
